import { ParameterApi } from '@app/api';
import {
  PARAMETER_TYPE,
  Parameter,
  parameterListValues
} from '@app/model/Parameter';
import { ApiError, toApiError } from '@app/utils/ApiError';
import * as React from 'react';
import { create } from 'zustand';

/**
 * The service's runtime settings, held once per session for anything that needs to read one.
 *
 * Not a createEntityStore, and not for want of trying: that helper is built on `id`, and a
 * parameter has none — the list is the service's *catalog* of settings, so a setting nobody has
 * overridden has no row to have an id. It is addressed by its constant name instead.
 *
 * Why cache at all: `GET /parameters` is open to any authenticated user precisely so the frontend
 * can configure itself from it, and a value read on a hot path — the page size of a search, the
 * roles allowed to edit a global filter — must not cost a round trip each time. Reads here are
 * synchronous and never throw; see the accessors at the bottom.
 *
 * Staleness is bounded two ways, and the belt matters as much as the braces:
 *
 * - Every write is pushed to every connected client over the entity websocket, so a change is
 *   normally reflected within milliseconds. EntityUpdateStream calls `invalidate` on a Parameter
 *   message, and reloads on reconnect so a change made while the socket was down is not missed.
 * - Failing that, STALE_AFTER_MS. This deliberately matches the service's own
 *   `parameter-cache.expire-after-write` of one minute: the backend already tolerates its own
 *   replicas disagreeing for that long, so a browser doing the same introduces no new window.
 */

/** Mirrors quarkus.cache.caffeine."parameter-cache".expire-after-write in the service. */
const STALE_AFTER_MS = 60_000;

interface ParameterStore {
  items: Parameter[];
  /** Constant name to parameter, rebuilt whenever items change. The lookup every read uses. */
  byName: Record<string, Parameter>;
  isLoaded: boolean;
  /** Why the last load failed, or null. Parked rather than thrown — see load(). */
  error: ApiError | null;
  /** When items were last filled, for STALE_AFTER_MS. 0 while never loaded. */
  loadedAt: number;

  /**
   * Fills the cache. Records failures on `error` instead of rejecting, so a view may call it
   * fire-and-forget, and collapses concurrent calls into the one request.
   *
   * @param forceRefresh ignores both the cache and its freshness. What the admin screen uses:
   *     that page exists to show what the service is using right now.
   */
  load: (forceRefresh?: boolean) => Promise<void>;

  /** Marks the cache stale without clearing it, so readers keep working until the reload lands. */
  invalidate: () => void;

  /** Replaces one parameter from what a write returned, so the writer's own tab is instantly right. */
  applyWrite: (parameter: Parameter) => void;

  isStale: () => boolean;
}

const indexByName = (items: Parameter[]): Record<string, Parameter> =>
  items.reduce<Record<string, Parameter>>((index, parameter) => {
    index[parameter.name] = parameter;
    return index;
  }, {});

// Outside the store: an in-flight request is not state anything renders, and keeping it here is
// what makes two components mounting at once share one request rather than race.
let inFlight: Promise<void> | null = null;

export const useParameterStore = create<ParameterStore>((set, get) => ({
  items: [],
  byName: {},
  isLoaded: false,
  error: null,
  loadedAt: 0,

  isStale: () => Date.now() - get().loadedAt > STALE_AFTER_MS,

  load: async (forceRefresh = false) => {
    if (!forceRefresh && get().isLoaded && !get().isStale()) return;

    // A forced refresh still joins a request already on the wire: it was issued no earlier than
    // this call, so its answer is just as current as a second request's would be.
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const items = await ParameterApi.getAll();
        set({ items, byName: indexByName(items), isLoaded: true, error: null, loadedAt: Date.now() });
      } catch (err) {
        // Left non-fatal on purpose. Every accessor takes a fallback, so a service that cannot be
        // asked leaves the frontend behaving exactly as it did before it read parameters at all.
        const error = toApiError(err);
        set({ error });
        console.warn(`[Parameters] Could not load the runtime settings: ${error.message}`);
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  },

  invalidate: () => set({ loadedAt: 0 }),

  applyWrite: (parameter) => {
    const items = get().items.map(p => (p.name === parameter.name ? parameter : p));
    set({ items, byName: indexByName(items) });
  }
}));

// ------------------------------------------------------------------------------------------
// Reading a value
// ------------------------------------------------------------------------------------------

/**
 * The catalog constants this frontend reads. Only the ones it actually uses are named — the
 * catalog is the service's, and listing all of it here would be a second copy to keep in step.
 * The accessors take any string, so nothing has to be added here to read a new key.
 */
export const PARAMETER = {
  /** Upper bound the service puts on ?size for /class/search. */
  SEARCH_MAX_PAGE_SIZE: 'SEARCH_MAX_PAGE_SIZE',
  /** Roles allowed to create, edit and delete global saved filters. */
  SAVED_FILTERS_GLOBAL_EDITION: 'SAVED_FILTERS_GLOBAL_EDITION',
  /** Roles allowed to publish and retract announcements. */
  NOTIFICATION_ANNOUNCE_ROLES: 'NOTIFICATION_ANNOUNCE_ROLES',
  /**
   * Countries that rest on Friday and Saturday, so a class's span is counted in the working week it
   * was really delivered in. Read by model/DeliveryPattern, which also carries the fallback.
   */
  WORKING_WEEK_FRIDAY_SATURDAY: 'WORKING_WEEK_FRIDAY_SATURDAY'
} as const;

/**
 * Checks a parameter is of the type the caller expects before converting it.
 *
 * A type mismatch is a programming error rather than bad data — the same judgement the service
 * makes, where reading a STRING key with getInt throws. It does not throw here: a frontend must
 * not lose a page over a setting, so it complains loudly and the caller's fallback stands.
 */
const ofType = (
  parameter: Parameter | undefined,
  name: string,
  expected: PARAMETER_TYPE
): Parameter | undefined => {
  if (!parameter) return undefined;

  if (parameter.type !== expected) {
    console.error(
      `[Parameters] ${name} is declared ${parameter.type} but was read as ${expected}. Using the fallback.`
    );
    return undefined;
  }
  return parameter;
};

/**
 * The conversions, one per type, as pure functions of a parameter that may not be there.
 *
 * Every one takes the value to use when the setting cannot be read — not loaded yet, absent from
 * this service's catalog, unparseable, or the wrong type. Pass what the code used before it
 * consulted the parameter at all: the worst case is then the old behaviour rather than a broken
 * page, and a read is safe during the very first render, before anything has been fetched.
 */
const CONVERT = {
  string: (parameter: Parameter | undefined, name: string, fallback: string): string =>
    ofType(parameter, name, PARAMETER_TYPE.STRING)?.value ?? fallback,

  int: (parameter: Parameter | undefined, name: string, fallback: number): number => {
    const raw = ofType(parameter, name, PARAMETER_TYPE.INTEGER)?.value;
    if (raw === undefined) return fallback;

    const parsed = Number(raw.trim());
    return Number.isInteger(parsed) ? parsed : fallback;
  },

  boolean: (parameter: Parameter | undefined, name: string, fallback: boolean): boolean => {
    const raw = ofType(parameter, name, PARAMETER_TYPE.BOOLEAN)?.value?.trim().toLowerCase();
    // Only the two spellings the service accepts. Anything else is not a boolean, so the caller's
    // fallback is a better answer than reading it as false and silently disabling something.
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  },

  stringList: (parameter: Parameter | undefined, name: string, fallback: string[]): string[] => {
    const raw = ofType(parameter, name, PARAMETER_TYPE.STRING_LIST)?.value;
    if (raw === undefined) return fallback;

    // Split the way the service splits it, blanks dropped. An override cleared down to "," yields
    // an empty list, which is a real answer — "no roles may do this" — not a missing value.
    return parameterListValues(raw);
  }
};

/**
 * Read a value from outside React: the API layer, an event handler, a module-level helper.
 *
 * Synchronous and non-reactive — it answers from the cache as it stands and does not subscribe,
 * so a later change does not re-render anything. That is the right trade at a call site that runs
 * once per action; in a render path use the hooks below instead.
 */
const currentByName = (name: string) => useParameterStore.getState().byName[name];

export const parameterString = (name: string, fallback: string) =>
  CONVERT.string(currentByName(name), name, fallback);

export const parameterInt = (name: string, fallback: number) =>
  CONVERT.int(currentByName(name), name, fallback);

export const parameterBoolean = (name: string, fallback: boolean) =>
  CONVERT.boolean(currentByName(name), name, fallback);

export const parameterStringList = (name: string, fallback: string[]) =>
  CONVERT.stringList(currentByName(name), name, fallback);

/**
 * The same reads for a component, subscribed to the one parameter they name.
 *
 * Selecting `byName[name]` rather than the whole store means a component re-renders when its own
 * setting changes and not when any other one does — which matters because a websocket message
 * replaces every parameter at once.
 */
export const useParameterString = (name: string, fallback: string) =>
  CONVERT.string(useParameterStore(state => state.byName[name]), name, fallback);

export const useParameterInt = (name: string, fallback: number) =>
  CONVERT.int(useParameterStore(state => state.byName[name]), name, fallback);

export const useParameterBoolean = (name: string, fallback: boolean) =>
  CONVERT.boolean(useParameterStore(state => state.byName[name]), name, fallback);

/**
 * A list is rebuilt on every call, so it cannot be returned straight from a selector without
 * giving zustand a new reference each render. The parameter is selected and split downstream.
 */
export const useParameterStringList = (name: string, fallback: string[]): string[] => {
  const parameter = useParameterStore(state => state.byName[name]);
  return React.useMemo(() => CONVERT.stringList(parameter, name, fallback),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parameter, name, fallback.join(',')]);
};
